import { useRef, useState } from "react"

import { Ban, Eye, Lock, ShieldCheck } from "lucide-react"
import { useStore } from "zustand"

import { Button } from "@/components/ui/button"
import { store } from "@/lib/store"
import BoundlessLogo from "@/svg/boundless/logo.svg?react"

// Mirrors the track's `gap-2.5` and `px-5.5` below. The counter derives the
// active card from the scroll offset, so both have to move together with
// those classes or it drifts.
const CARD_GAP_PX = 10
const TRACK_PADDING_PX = 22

const lenderMatches = [
  { nameWidth: "78px", product: "Term loan · 12–36 mo", match: "91%" },
  { nameWidth: "104px", product: "Line of credit", match: "88%" },
]

const guarantees = [
  {
    icon: Lock,
    title: "No credit pull to start",
    description: "Nothing hits your report until you accept an offer you like.",
  },
  {
    icon: Eye,
    title: "Read-only bank access",
    description: "We verify revenue. We can never move money.",
  },
  {
    icon: ShieldCheck,
    title: "Bank-grade encryption",
    description: "256-bit in transit and at rest, on audited infrastructure.",
  },
  {
    icon: Ban,
    title: "Never sold or spammed",
    description:
      "Your details go to matched lenders only — not a marketing list.",
  },
]

/**
 * The compact twin of `QualificationPanel`'s card. It keeps the same content
 * but every dimension is redrawn for a phone, so the two are kept as separate
 * markup rather than one component threaded with a size variant.
 */
function LenderMatchesCard() {
  return (
    <div className="mx-5.5 rounded-2xl bg-white px-[15px] py-3 shadow-[0_20px_40px_-20px_rgba(9,20,60,0.55)]">
      <div className="mb-[11px] flex items-center justify-between">
        <div className="text-[13.5px] font-semibold text-[#16265C]">
          Lenders matching your profile
        </div>
        <div className="text-brand-accent flex items-center gap-1.5 text-[11.5px] font-semibold">
          <span className="animate-panel-pulse bg-brand-accent size-1.5 rounded-full motion-reduce:animate-none" />
          Preview
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        {lenderMatches.map(({ nameWidth, product, match }) => (
          <div
            key={product}
            className="flex items-center gap-2.5 rounded-[11px] bg-[#F5F7FB] px-2.5 py-[7px]"
          >
            <div
              aria-hidden="true"
              className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-[#DDE3F1] text-[11px] font-bold text-[#5A6A9C]"
            >
              ◼
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
              <div
                className="h-2 rounded-[4px] bg-[#C9D1E6]"
                style={{ width: nameWidth }}
              />
              <div className="text-[11px] text-[#8A93A8]">{product}</div>
            </div>
            <div className="bg-brand-accent-soft text-brand-accent-strong shrink-0 rounded-[11px] px-2 py-[3px] text-[11.5px] font-semibold whitespace-nowrap">
              {match} match
            </div>
          </div>
        ))}
      </div>

      <div className="mt-[9px] flex items-start gap-2 border-t border-[#EDF0F6] pt-[9px] text-[11.5px] leading-[1.4] text-[#5B6478]">
        <Lock
          className="text-primary mt-px size-3.5 shrink-0"
          strokeWidth={1.7}
        />
        Names, rates and terms unlock as soon as your account exists.
      </div>
    </div>
  )
}

/**
 * Horizontally swipeable guarantees, built on CSS scroll snapping rather than
 * a carousel library: there is no autoplay, no looping and no controls to
 * drive, so the browser's own scrolling is the whole behaviour.
 */
function GuaranteeCarousel() {
  const trackRef = useRef<HTMLDivElement>(null)
  const [currentIndex, setCurrentIndex] = useState(0)

  // Derive the counter from the scroll offset instead of tracking it
  // ourselves, so a flick that overshoots still reports the card the browser
  // actually snapped to.
  function handleScroll() {
    const track = trackRef.current
    const firstCard = track?.firstElementChild
    if (!track || !(firstCard instanceof HTMLElement)) {
      return
    }
    const stride = firstCard.offsetWidth + CARD_GAP_PX
    const index = Math.round(track.scrollLeft / stride)
    const clamped = Math.min(Math.max(index, 0), guarantees.length - 1)

    // Momentum scrolling fires this continuously; only the card actually
    // changing is worth a render.
    setCurrentIndex((current) => (current === clamped ? current : clamped))
  }

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex items-center justify-between px-5.5">
        <div className="text-[11.5px] font-semibold tracking-[0.14em] text-white/55 uppercase">
          Before you start
        </div>
        <div aria-live="polite" className="text-[11.5px] text-white/45">
          Swipe · {currentIndex + 1} of {guarantees.length}
        </div>
      </div>

      <div
        ref={trackRef}
        onScroll={handleScroll}
        // The cards hold no focusable content, so without this a keyboard user
        // cannot scroll the track to reach the last ones.
        tabIndex={0}
        role="group"
        aria-label="Guarantees"
        className="flex snap-x snap-mandatory gap-2.5 overflow-x-auto scroll-smooth px-5.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        style={{ scrollPaddingLeft: `${TRACK_PADDING_PX}px` }}
      >
        {guarantees.map(({ icon: Icon, title, description }) => (
          <div
            key={title}
            className="flex w-[87%] shrink-0 snap-start items-start gap-[11px] rounded-[14px] border border-white/15 bg-white/8 px-[15px] py-[13px]"
          >
            <Icon
              className="mt-0.5 size-[18px] shrink-0 text-white"
              strokeWidth={1.6}
            />
            <div className="flex flex-col gap-[3px]">
              <div className="text-[14.5px] font-semibold text-white">
                {title}
              </div>
              <div className="text-[12.5px] leading-[1.4] text-white/70">
                {description}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * The mobile-only proof screen that precedes the signup form. The button moves
 * on to the form, and the header link sends a returning borrower to sign-in.
 */
export function MobileProofScreen({
  onCreateAccount,
  headerLink,
}: {
  onCreateAccount: () => void
  headerLink: React.ReactNode
}) {
  const partnerLogo = useStore(store, (state) => state.partnerLogo)

  return (
    <div className="bg-primary relative flex min-h-0 flex-1 flex-col overflow-hidden lg:hidden">
      <div className="animate-panel-drift pointer-events-none absolute -top-30 -right-30 size-115 rounded-full bg-[radial-gradient(circle_at_38%_38%,rgba(255,255,255,0.14),rgba(255,255,255,0)_66%)] motion-reduce:animate-none" />

      <div className="relative flex min-h-0 flex-1 flex-col overflow-y-auto">
        <div className="flex items-center justify-between px-5.5 pt-4">
          {/* The logo's fills are baked in, so the fallback is forced to white
              rather than recoloured. */}
          {partnerLogo ? (
            <img
              src={partnerLogo}
              alt="Partner logo"
              className="h-5.5 w-auto"
            />
          ) : (
            <BoundlessLogo className="w-28 brightness-0 invert" />
          )}
          {headerLink}
        </div>

        <div className="flex-1" />

        <div className="flex flex-col items-center gap-3 px-5.5 pt-2.5 text-center">
          <h1 className="text-[28px] leading-[1.12] font-bold tracking-[-0.03em] text-pretty text-white [@media(max-height:700px)]:text-[24px]">
            See what you qualify for before you commit to anything.
          </h1>
          <p className="text-[14px] leading-[1.45] text-pretty text-white/80">
            One application.{" "}
            <strong className="font-semibold text-white">
              180+ vetted lenders
            </strong>{" "}
            review it in real time and come back with offers.
          </p>
        </div>

        <div className="mt-3.5 [@media(max-height:700px)]:mt-2.5">
          <LenderMatchesCard />
        </div>

        <div className="mt-4 [@media(max-height:700px)]:mt-2.5">
          <GuaranteeCarousel />
        </div>

        <div className="flex-1" />

        {/* The design is drawn at 390x844. On a shorter phone the fixed
            rhythm above lands a couple of pixels past the fold, which reads as
            a broken scroll rather than a scroll, so the most generous padding
            on the screen gives way first. A phone with its browser toolbars
            showing is shorter still, so there the headline and the gaps
            between blocks tighten too. */}
        <div className="flex flex-col items-center gap-2.5 px-5.5 pt-6 pb-7.5 [@media(max-height:700px)]:pt-3 [@media(max-height:700px)]:pb-4">
          <Button
            type="button"
            onClick={onCreateAccount}
            className="text-primary h-12.5 w-full bg-white text-[15px] font-semibold hover:bg-white/90"
          >
            Create account
          </Button>
          <div className="text-[12.5px] text-white/60">
            Takes two minutes to complete.
          </div>
        </div>
      </div>
    </div>
  )
}
